import { useState } from "react";
import { Clock, ChevronLeft, ChevronRight, Loader2, MessageSquare } from "lucide-react";
import { Link } from "react-router";
import { ANSWER_CONFIDENCE_LABELS, type QuestionSummary } from "@gb/contracts";
import { EmptyState } from "@/components/gb/empty-state";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useStillAnswering } from "./answer-wait";
import { useQuestions } from "./chat-api";
import { DISPLAY_LOCALE } from "@/lib/format";

/** Ten rows, as the design has it. */
const PAGE_SIZE = 10;

/**
 * What has been asked of this project's Brain: the History tab.
 *
 * **A tab of its own, beside Ask** (the owner, 2026-09-27, `product/0013`). It
 * used to sit below the ask box (`product/0005` §2.4), which is why the Ask
 * tab's answer panel had a fixed height: an answer growing as it was written
 * pushed this list down the page. The Ask tab now holds only the question
 * being asked, and a row here opens its question there.
 *
 * **Not in the rail, either.** The sidebar used to list conversations by
 * title and that is what this replaced: a 200px rail can show a title and
 * nothing else, so it could not say what an answer was built from — which is
 * the only thing that distinguishes two questions worth telling apart. See
 * `product/0005-a-question-is-answered-once.md`.
 *
 * Every row is a link rather than a button. These are addressable records, and
 * a reader who wants one in a second tab should be able to have it.
 */
export function QuestionHistory({ projectId }: { projectId: string }) {
  const [page, setPage] = useState(0);
  const { data, isPending, isError } = useQuestions(projectId, {
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });
  // **Still being answered, from what the server says.** This tab has no
  // stream: an answer asked on the Ask tab runs on at the server when the tab
  // is left, and a reload or another tab never had one. So a question with no
  // answer, asked less than 25 minutes ago, reads "Answering…" (2026-09-27).
  // Without it such a row read "No answer", as if the answer had failed.
  const answering = useStillAnswering(data?.questions);

  if (isError) {
    return (
      <p role="alert" className="text-[0.92rem] font-semibold text-danger">
        The questions could not be loaded.
      </p>
    );
  }

  if (isPending || !data) {
    return (
      <div className="flex items-center gap-2 py-16 text-muted" role="status">
        <Loader2 size={16} className="animate-spin" aria-hidden />
        <span className="text-[0.9rem]">Loading questions…</span>
      </div>
    );
  }

  // Said out loud on its own tab. Below the ask box, an empty list rendered
  // nothing, because the box above it already said what to do; here nothing
  // else on the screen does.
  if (data.total === 0) {
    return (
      <EmptyState
        icon={MessageSquare}
        title="No questions yet"
        description="Every question asked of this project’s Brain is kept here, with what its answer was built from."
        action={
          <Button asChild>
            <Link to={`/projects/${projectId}/brain/ask`}>Ask a question</Link>
          </Button>
        }
      />
    );
  }

  const { questions, total } = data;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const first = page * PAGE_SIZE + 1;
  const last = page * PAGE_SIZE + questions.length;

  return (
    <section
      aria-labelledby="question-history-heading"
      className="rounded-card-lg border-[1.5px] border-border bg-surface"
    >
      <div className="flex items-center gap-2 px-5 py-3.5">
        <span className="flex text-muted">
          <Clock size={14} />
        </span>
        <h2
          id="question-history-heading"
          className="font-display text-[0.66rem] font-semibold tracking-[0.12em] text-ink uppercase"
        >
          Question history
        </h2>
        {/* The count sits in the header rather than only in the footer, so the
            size of the thing is known before scrolling to the bottom of it. */}
        <span className="ml-auto text-[0.78rem] text-muted">
          {total === 1 ? "1 question" : `${total} questions`}
        </span>
      </div>

      <ul className="border-t border-border">
        {questions.map((question) => (
          <li
            key={question.id}
            className="border-b border-border last:border-b-0"
          >
            <Row
              projectId={projectId}
              question={question}
              answering={answering.has(question.id)}
            />
          </li>
        ))}
      </ul>

      {pages > 1 && (
        <div className="flex items-center gap-3 border-t border-border px-5 py-3">
          {/* Says which rows these are, not just which page. "31–40 of 96" is
              checkable against the list; "page 4" is not. */}
          <span className="text-[0.8rem] text-muted">
            {first}&ndash;{last} of {total}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            >
              <ChevronLeft aria-hidden size={15} />
              Newer
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              // Bounded by `total` rather than by "the last page came back
              // full": a page that happens to be exactly `PAGE_SIZE` long is
              // not evidence that another one exists.
              disabled={last >= total}
              onClick={() => setPage((current) => current + 1)}
            >
              Older
              <ChevronRight aria-hidden size={15} />
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function Row({
  projectId,
  question,
  answering,
}: {
  projectId: string;
  question: QuestionSummary;
  answering: boolean;
}) {
  return (
    <Link
      to={`/projects/${projectId}/brain/ask/${question.id}`}
      className={cn(
        "flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-subtle",
      )}
    >
      {/* What somebody committed to, against what was merely asked. Carried on
          the accessible name rather than by colour alone — a filled dot and a
          hollow one are the same shape to a screen reader, and green against
          grey is the pair most often indistinguishable. */}
      <span
        aria-hidden
        className={cn(
          "size-2 shrink-0 rounded-full",
          question.decisionId ? "bg-ok" : "bg-border",
        )}
      />
      <span className="min-w-0 flex-1">
        {question.decisionId && (
          <span className="sr-only">Accepted as a decision. </span>
        )}
        <span className="block truncate text-[0.95rem] font-semibold text-ink">
          {question.question}
        </span>
        <span className="mt-0.5 block text-[0.82rem] text-muted">
          {/* A question in flight has no answer *yet*, which is not the same as
              having failed — and `basis` says "No answer" for both. It appears
              here the instant it is asked, so this is the state it is in for
              as long as its answer is being written. */}
          {answering ? "Answering…" : basis(question)}
        </span>
      </span>
      <span className="shrink-0 text-[0.82rem] text-muted">
        {askedLabel(question.askedAt)}
      </span>
      <span className="flex shrink-0 text-muted">
        <ChevronRight size={16} />
      </span>
    </Link>
  );
}

/**
 * What the answer was built from, in one line.
 *
 * Four states, and the distinction between the last two is the point:
 * *"No answer"* means the answering failed or was stopped, and *"No record
 * found"* means it ran and this project holds nothing that bears on the
 * question. Rendering both as an absence would hide a failure behind a finding
 * — the difference `document-ai/0003` §2.4 already insists on in the prose.
 */
export function basis(question: QuestionSummary): string {
  if (!question.answered) return "No answer";
  if (question.sourceCount === 0) return "No record found";

  const counted =
    question.sourceCount === 1
      ? // Named by its citation when there is one source to name. With several
        // there is no single citation, and the confidence is the more useful
        // second half anyway.
        question.citation
        ? `1 source · ${question.citation}`
        : "1 source"
      : `${question.sourceCount} sources`;

  // Appended, not substituted: the count says what it was built from and the
  // level says how well that held up, and a row that shows only one of them
  // answers half the question a reader is scanning for. Absent when nothing
  // assessed it — "not assessed" is not a level.
  return question.confidence
    ? `${counted} · ${ANSWER_CONFIDENCE_LABELS[question.confidence]}`
    : counted;
}

/**
 * When it was asked, at the precision a reader can use.
 *
 * Recent questions get a clock because "this morning or after lunch?" is a real
 * distinction while you still remember asking; older ones get a date, because
 * by then the time of day tells you nothing and the date is what you are
 * scanning for.
 *
 * `now` is injectable so the boundaries are testable without freezing the
 * clock globally.
 */
export function askedLabel(iso: string, now: Date = new Date()): string {
  const asked = new Date(iso);
  if (Number.isNaN(asked.getTime())) return "";

  const seconds = (now.getTime() - asked.getTime()) / 1000;
  if (seconds < 60) return "Just now";

  const time = asked.toLocaleTimeString(DISPLAY_LOCALE, {
    hour: "2-digit",
    minute: "2-digit",
  });
  // Compared on the calendar day rather than on elapsed hours: something asked
  // at 23:50 is "Yesterday" at 00:10, not "17 minutes ago dressed up as today".
  const days = daysApart(asked, now);
  if (days === 0) return `Today · ${time}`;
  if (days === 1) return `Yesterday · ${time}`;

  return asked.toLocaleDateString(DISPLAY_LOCALE, {
    day: "numeric",
    month: "short",
  });
}

/**
 * When a question was asked, for the question's own heading on the Ask tab:
 * always with the time (2026-09-27, `product/0013`).
 *
 * The list drops the time after yesterday, because a reader scanning rows is
 * looking for the day. A reader looking at one question can use both, and the
 * year once it is not this one.
 *
 * **Never "Just now".** The list says that for the first minute; the heading
 * shows the clock from the first second. The first minute is exactly when a
 * question has just been asked and the heading is read. And nothing renders
 * the heading again on a timer: once the answer is stored, the screen stops
 * polling, so an answer that took less than a minute left "Just now" there
 * for as long as the page stayed open.
 */
export function askedAtLabel(iso: string, now: Date = new Date()): string {
  const asked = new Date(iso);
  if (Number.isNaN(asked.getTime())) return "";

  const time = asked.toLocaleTimeString(DISPLAY_LOCALE, {
    hour: "2-digit",
    minute: "2-digit",
  });
  const days = daysApart(asked, now);
  if (days === 0) return `Today · ${time}`;
  if (days === 1) return `Yesterday · ${time}`;

  const date = asked.toLocaleDateString(DISPLAY_LOCALE, {
    day: "numeric",
    month: "short",
    ...(asked.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
  return `${date} · ${time}`;
}

/** Whole calendar days between two instants, in the viewer's own timezone. */
function daysApart(earlier: Date, later: Date): number {
  const startOf = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  return Math.round((startOf(later) - startOf(earlier)) / 86_400_000);
}
